import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Alert, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { LoginRequest } from '../types/auth.types';

const { Title } = Typography;

// フォームバリデーション定数
const VALIDATION_RULES = {
  USERNAME_MIN_LENGTH: 3,
  PASSWORD_MIN_LENGTH: 8,
} as const;

// UI定数
const UI_CONSTANTS = {
  TITLE_LEVEL: 2,
} as const;

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated, error, clearError } = useAuthStore();
  const [form] = Form.useForm<LoginRequest>();

  // 認証済みの場合はダッシュボードへリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values: LoginRequest) => {
    try {
      await login(values);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md shadow-lg">
        <div className="text-center mb-8">
          <Title level={UI_CONSTANTS.TITLE_LEVEL}>Faro Console</Title>
          <p className="text-gray-600">クリアショット吹付装置監視システム</p>
        </div>

        {error && (
          <Alert
            message="ログインエラー"
            description={error}
            type="error"
            showIcon
            closable
            onClose={clearError}
            className="mb-4"
          />
        )}

        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            label="ユーザー名"
            name="userName"
            rules={[
              { required: true, message: 'ユーザー名を入力してください' },
              { min: VALIDATION_RULES.USERNAME_MIN_LENGTH, message: `ユーザー名は${VALIDATION_RULES.USERNAME_MIN_LENGTH}文字以上である必要があります` },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="ユーザー名"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item
            label="パスワード"
            name="password"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: VALIDATION_RULES.PASSWORD_MIN_LENGTH, message: `パスワードは${VALIDATION_RULES.PASSWORD_MIN_LENGTH}文字以上である必要があります` },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="パスワード"
              size="large"
              disabled={isLoading}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isLoading}
              className="w-full"
            >
              ログイン
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};